import type { InspectionViewContext } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import type {
  ReportSelectionAction,
  ReportView,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/reportViewSchema'
import isEqual from 'lodash/isEqual'
import { useEffect, useMemo, useState } from 'react'

const identities = new WeakMap<ReportView, symbol>()
function reportIdentity(report: ReportView) {
  let identity = identities.get(report)
  if (!identity) {
    identity = Symbol()
    identities.set(report, identity)
  }
  return identity
}

type Selection = { sourceId: string; revision: number }

export function useReportViewerFocus(
  report: ReportView,
  defaultKey: string,
  publish?: (context: InspectionViewContext | null) => void
) {
  const identity = reportIdentity(report)
  const [state, setState] = useState<{
    identity: symbol
    focusedKey: string
    revision: number
    selection?: Selection
    contexts: Map<string, InspectionViewContext | null>
  }>({ identity, focusedKey: defaultKey, revision: 0, contexts: new Map() })
  if (state.identity !== identity) {
    setState({
      identity,
      focusedKey: defaultKey,
      revision: 0,
      contexts: new Map(),
    })
  }
  const callbacks = useMemo(() => {
    const cache = new Map<
      string,
      (context: InspectionViewContext | null) => void
    >()
    return (key: string) => {
      let callback = cache.get(key)
      if (!callback) {
        callback = (context) =>
          setState((previous) => {
            if (
              previous.identity !== identity ||
              isEqual(previous.contexts.get(key), context)
            )
              return previous
            const contexts = new Map(previous.contexts)
            contexts.set(key, context)
            return { ...previous, contexts }
          })
        cache.set(key, callback)
      }
      return callback
    }
  }, [identity])
  const context = state.contexts.get(state.focusedKey) ?? null
  useEffect(() => {
    publish?.(context)
  }, [context, publish])
  useEffect(() => () => publish?.(null), [publish])
  const focusOn = (focusedKey: string) =>
    setState((previous) => ({ ...previous, focusedKey }))
  const selectSource = (action: ReportSelectionAction) =>
    setState((previous) => {
      const revision = previous.revision + 1
      return {
        ...previous,
        revision,
        focusedKey: `selection-${revision}`,
        selection: { sourceId: action.asset_source_id, revision },
      }
    })
  const dismissSelection = () =>
    setState((previous) => ({
      ...previous,
      focusedKey: defaultKey,
      selection: undefined,
    }))
  return {
    focusedKey: state.focusedKey,
    selection: state.selection,
    focusOn,
    selectSource,
    dismissSelection,
    onContext: callbacks,
  }
}
